import { readinessStateSchema, type ChecklistItem, type ChecklistTiming, type PersonChecklist, type ReadinessState, type RequirementStatus, type ReviewCause } from '@vigeo/shared';
import type { ComplianceTone } from '@vigeo/ui';

/**
 * How derived state becomes words and colour — in ONE place (ADR-006).
 *
 * Every registry here is `satisfies Record<Union, Presenter>`, so adding a state, status
 * or timing is a compile error naming the exact id until someone decides how it should
 * read. That is the mechanism that stops a new state quietly rendering as a blank chip.
 *
 * No component may branch on a status or state itself; it looks the presenter up.
 */

export interface StatePresenter {
  readonly label: string;
  readonly tone: ComplianceTone;
  /** Whether this state permits a first assignment. */
  readonly canStart: boolean;
  /** One line telling HR what this state means for them, in their words. */
  readonly meaning: string;
  /** What to do next. Empty when there is nothing to do. */
  readonly nextStep: string;
}

/**
 * `attention` rather than `warning` for needs_review is the whole point of the fourth
 * tone: "ready with exceptions" and "needs review" are operationally opposite — one person
 * may start work, the other may not — and sharing amber would make them look alike.
 */
export const statePresenters = {
  ready: {
    label: 'Ready',
    tone: 'good',
    canStart: true,
    meaning: 'Configured clearance checks are met. HR must verify the allowed duties and record the final decision.',
    nextStep: '',
  },
  not_ready: {
    label: 'Not ready',
    tone: 'critical',
    canStart: false,
    meaning: 'Something required is missing, and we know what.',
    nextStep: 'Chase the outstanding items below.',
  },
  ready_with_exceptions: {
    label: 'Ready with exceptions',
    tone: 'warning',
    canStart: true,
    meaning: 'Some requirements have exceptions. This is not final authorization to work.',
    nextStep: 'The waived items still count as unmet and remain on the record.',
  },
  needs_review: {
    label: 'Needs review',
    tone: 'attention',
    canStart: false,
    meaning: 'This one is on us — either something is waiting for a decision, or the record cannot be trusted.',
    nextStep: 'Review the items flagged below; chasing the candidate will not clear this.',
  },
  /**
   * Never `good`: the whole defect this state exists to fix was an empty checklist rendering
   * green (known-limitations #41), because nothing was outstanding when nothing had been
   * asked. `canStart: false` is the operative field — it keeps an unassessed person out of
   * first-assignment eligibility everywhere that reads a presenter instead of testing the
   * state itself.
   *
   * `warning` rather than `attention`, and the reason is a constraint worth stating: no two
   * states may share BOTH a tone and a start permission (`tests/presenters.test.ts`), and
   * `attention`/false is already how "Needs review" reads. Of the pairs left, `warning`/false
   * is the only honest one — amber here means "incomplete", not "nearly there", and the
   * permission is what separates it from "Ready with exceptions", which is also amber but may
   * start. The label carries the meaning; the colour only has to avoid claiming this is fine.
   */
  setup_required: {
    label: 'Setup required',
    tone: 'warning',
    canStart: false,
    meaning: 'No job profile is assigned, so there is nothing to assess this person against.',
    nextStep: 'Assign a job profile to determine readiness.',
  },
} as const satisfies Record<ReadinessState, StatePresenter>;

/**
 * EVERY readiness state, in the order the 06b frames band them, so a list of bands is a PARTITION
 * and the figures sum to the roster.
 *
 * Frame 0.1 draws four (`290:431`); `readinessStateSchema` has five. `setup_required` is reachable
 * and real — nobody's job profile, or an empty resolved scope — and writing the frame's four by
 * hand left anyone in that state counted in the total, present in the table, in no band, with
 * nothing on screen explaining the shortfall. The tail is derived from the schema rather than
 * listed, so a sixth state cannot silently vanish from a band list again.
 *
 * Shared by the Roster page and the dashboard's Roster card: one ordering, so the same roster
 * cannot band differently on two screens.
 */
export const readinessBandOrder: readonly ReadinessState[] = (() => {
  const drawn: readonly ReadinessState[] = ['ready', 'ready_with_exceptions', 'not_ready', 'needs_review'];
  return [...drawn, ...readinessStateSchema.options.filter((state) => !drawn.includes(state))];
})();

export interface StatusPresenter {
  readonly label: string;
  readonly tone: ComplianceTone;
}

/**
 * `indeterminate` is deliberately `attention`, never `critical`.
 *
 * On a fresh nurse's file ten rows arrive indeterminate because the agency never settled
 * their interval. Painting those red opens every personnel record with ten alarms about a
 * policy gap nobody in HR can act on, and trains people to ignore the colour.
 */
export const statusPresenters = {
  not_applicable: { label: 'Not applicable', tone: 'good' },
  not_started: { label: 'Not started', tone: 'critical' },
  scheduled: { label: 'Scheduled — not yet due', tone: 'attention' },
  in_progress: { label: 'In progress', tone: 'warning' },
  awaiting_review: { label: 'Awaiting review', tone: 'attention' },
  satisfied: { label: 'Complete', tone: 'good' },
  expiring_soon: { label: 'Expiring soon', tone: 'warning' },
  expired: { label: 'Expired', tone: 'critical' },
  rejected: { label: 'Rejected', tone: 'critical' },
  indeterminate: { label: 'Cannot assess', tone: 'attention' },
} as const satisfies Record<RequirementStatus, StatusPresenter>;

export interface TimingPresenter {
  readonly label: string;
  /** Whether this item can ever be late. `on-event` and `unsettled` never can. */
  readonly canBeOverdue: boolean;
  readonly note: string;
}

/**
 * Without this, a one-time form and an event-triggered record are indistinguishable, and
 * separation paperwork reads as an open task on every active employee forever.
 */
export const timingPresenters = {
  dated: { label: 'Renews', canBeOverdue: true, note: '' },
  once: { label: 'One time', canBeOverdue: true, note: '' },
  'on-event': {
    label: 'Only if it happens',
    canBeOverdue: false,
    note: 'Recorded when the event occurs — not an outstanding task.',
  },
  unsettled: {
    label: 'No interval set',
    canBeOverdue: false,
    note: 'The policy does not state how often this is due. Awaiting an HR decision.',
  },
} as const satisfies Record<ChecklistTiming, TimingPresenter>;

/**
 * The three bands a checklist is grouped into.
 *
 * Grouping comes from DATA — `owner` and whether the item gates — never from a
 * requirement key, which `scripts/check-requirement-keys.mjs` would reject anyway. Band A
 * is exactly the row set the readiness fold reads, so the group header and the verdict
 * cannot disagree.
 */
export const checklistBands = ['blocking', 'required', 'agency'] as const;
export type ChecklistBand = (typeof checklistBands)[number];

export interface BandPresenter {
  readonly label: string;
  readonly description: string;
  /** Bands after the first start collapsed: the blocking band is the answer HR came for. */
  readonly defaultOpen: boolean;
}

export const bandPresenters = {
  blocking: {
    label: 'Holds up a first assignment',
    description: 'These decide whether this person can start.',
    defaultOpen: true,
  },
  required: {
    label: 'Required, not blocking',
    description: 'Additional records and ongoing obligations. Review their applicability, due dates and assigned owners.',
    defaultOpen: false,
  },
  agency: {
    label: 'Agency responsibilities',
    description: 'Shown for visibility. The agency owns these, not this person, and they count toward nobody’s progress.',
    defaultOpen: false,
  },
} as const satisfies Record<ChecklistBand, BandPresenter>;

export function bandOf(item: ChecklistItem): ChecklistBand {
  if (item.owner === 'agency' && item.gates.length === 0) return 'agency';
  return item.gates.length > 0 ? 'blocking' : 'required';
}

/* ------------------------------------------------------------------------------------------
 * Person Overview derivations (checkpoint 5). Every value the overview shows is either a
 * field of the ONE derived `PersonChecklist` or one of the pure functions below. Nothing
 * here is stored; nothing here recomputes readiness — these are projections of the
 * engine's result, in one documented place, so the page and the summary cannot disagree.
 * ---------------------------------------------------------------------------------------- */


/** Why a person is at "Needs review" — one line per cause, in HR's words. */
export const reviewCausePresenters = {
  awaiting_approval: { label: 'A submission is awaiting approval', detail: 'The candidate has done their part; the decision is ours.' },
  conflicting_evidence: { label: 'The evidence conflicts', detail: 'Two live records disagree; the engine will not guess which is right.' },
  impossible_dates: { label: 'The dates are impossible', detail: 'An expiry on or before the issue date, or a required date is missing.' },
  unresolvable_rule: { label: 'A policy decision is needed', detail: 'Resolve the stated policy question, recurrence or evidence mapping before treating this requirement as complete.' },
  missing_trigger_input: { label: 'A trigger question is unanswered', detail: 'Whether this requirement applies depends on an answer nobody has given yet.' },
} as const satisfies Record<ReviewCause, { readonly label: string; readonly detail: string }>;

export type ItemOwner = 'hr-review' | 'hr-decision' | 'hr-record' | 'agency' | 'candidate';

export interface OwnerPresenter {
  readonly label: string;
}

export const ownerPresenters = {
  'hr-review': { label: "On HR's desk" },
  'hr-decision': { label: 'Needs an HR decision' },
  'hr-record': { label: 'HR records this' },
  agency: { label: 'Agency' },
  candidate: { label: 'Waiting on the candidate' },
} as const satisfies Record<ItemOwner, OwnerPresenter>;

/**
 * Who must act next on an open row. Derived from data the wire already carries; the order
 * matters — a submitted step is HR's regardless of anything else about the row.
 */
export function ownerOf(item: ChecklistItem): ItemOwner {
  if (item.owner === 'agency' && item.gates.length === 0) return 'agency';
  if (item.stepState === 'submitted') return 'hr-review';
  if (item.status === 'awaiting_review' && item.compliance?.evidenceMode !== undefined && item.compliance.evidenceMode !== 'standard' && item.directlyRecordable) return 'hr-record';
  if (item.status === 'awaiting_review') return 'hr-review';
  if (item.issues.length > 0 || item.status === 'indeterminate') return 'hr-decision';
  // A step the candidate holds — issued to them, sent, in progress, or sent back for
  // correction — is theirs to finish. HR being ABLE to record it does not make it HR's next
  // move; an issued packet they have not opened yet is a reason to chase, not to record.
  if (item.stepState === 'not_started' || item.stepState === 'sent' || item.stepState === 'in_progress' || item.stepState === 'rejected') return 'candidate';
  if (item.directlyRecordable) return 'hr-record';
  return 'candidate';
}

const openStatuses: ReadonlySet<RequirementStatus> = new Set(['satisfied', 'not_applicable', 'expiring_soon', 'scheduled']);

/**
 * Every gating row that is not yet met — the rows that hold up a first assignment. This is
 * deliberately wider than `checklist.blockers`: the engine files a submitted or
 * undecidable gating row under `reviewItems` (its verdict is "Needs review", not "Not
 * ready"), but the person still cannot start until HR acts on it, and HR asked "what is
 * holding this person up", not "which rows failed". Order is the wire's.
 */
export function openBlockers(checklist: PersonChecklist): readonly ChecklistItem[] {
  return checklist.items.filter((item) => item.gates.length > 0 && !openStatuses.has(item.status));
}

export interface OverviewSummary {
  readonly met: number;
  readonly scorable: number;
  readonly unscorable: number;
  readonly blockers: number;
  readonly onHrDesk: number;
  readonly exceptions: number;
}

export function summaryOf(checklist: PersonChecklist): OverviewSummary {
  const { met, total, unscorable } = checklist.progress;
  return {
    met,
    scorable: total - unscorable,
    unscorable,
    blockers: openBlockers(checklist).length,
    onHrDesk: checklist.reviewItems.length,
    exceptions: checklist.appliedExceptions.length,
  };
}

export type NextActionKind = 'review' | 'answer' | 'record' | 'chase' | 'none';

export interface NextHrAction {
  readonly kind: NextActionKind;
  readonly title: string;
  readonly detail: string;
  /** The row the action is about, when there is one. */
  readonly item: ChecklistItem | null;
}

/**
 * The single most useful thing HR can do next. Precedence, in words: decide what is
 * already on your desk before asking anyone for anything; record what only HR can record;
 * only then chase the candidate. Nothing is stored — change the data and this changes.
 */
export function nextHrAction(checklist: PersonChecklist): NextHrAction {
  const blockers = openBlockers(checklist);
  const review = blockers.find((item) => ownerOf(item) === 'hr-review');
  if (review) {
    return { kind: 'review', title: `Review the ${review.label} submission`, detail: 'The candidate has done their part. Approving it moves this person forward.', item: review };
  }
  const decision = blockers.find((item) => ownerOf(item) === 'hr-decision');
  if (decision) {
    return { kind: 'answer', title: decision.compliance?.reviewQuestion ? `Resolve the policy decision for ${decision.label}` : `Review the missing information for ${decision.label}`, detail: decision.compliance?.reviewQuestion ?? 'Review the stated missing facts, dates or applicability answers before clearing this requirement.', item: decision };
  }
  const record = blockers.find((item) => ownerOf(item) === 'hr-record');
  if (record) {
    return { kind: 'record', title: `Record ${record.label}`, detail: 'The candidate has nothing to submit for this; HR records that it happened.', item: record };
  }
  const waiting = blockers.filter((item) => ownerOf(item) === 'candidate');
  if (waiting.length > 0) {
    return { kind: 'chase', title: `Waiting on the candidate — ${waiting.length} blocking item${waiting.length === 1 ? '' : 's'} outstanding`, detail: 'Nothing here is HR’s to do until they submit. If their link has lapsed, send a fresh one.', item: waiting[0] ?? null };
  }
  const state = statePresenters[checklist.state];
  return { kind: 'none', title: state.canStart ? 'Nothing on HR’s desk' : state.meaning, detail: state.canStart ? 'The configured checks are met. Record the final HR clearance and any restrictions before assigning work.' : state.nextStep, item: null };
}

/**
 * One line for a directory card or row (checkpoint 7B): the open blocker count and the
 * next HR action, taken from the same projections the Person Overview shows, so the
 * directory can never disagree with the page it opens. The "chase" title already carries
 * the count, so it is not repeated.
 */
export function directoryLine(checklist: PersonChecklist): string {
  /**
   * One state gets the instruction rather than the explanation. In a list a reader is
   * scanning for what to do, and "No job profile is assigned, so there is nothing to assess
   * this person against" states a situation they can already see from the missing role. The
   * Person Overview still shows both sentences, because there they have room to read.
   *
   * Scoped to this state on purpose: `nextHrAction`'s fallback serves every non-startable
   * verdict, and swapping title for next step there would rewrite the line for "Needs review"
   * too, which is not what was asked for.
   */
  if (checklist.state === 'setup_required') return statePresenters.setup_required.nextStep;

  const action = nextHrAction(checklist);
  const blockers = openBlockers(checklist).length;
  if (action.kind === 'chase' || blockers === 0) return action.title;
  return `${blockers} blocking · ${action.title}`;
}
